import React, { useState, useEffect, useRef } from 'react';
import {
  IonPage, IonContent, IonHeader, IonToolbar, IonButton, IonIcon,
  IonSpinner, useIonAlert,
} from '@ionic/react';
import {
  calendarOutline, timeOutline, addOutline, closeOutline,
  personOutline, locationOutline, constructOutline, peopleOutline,
  cubeOutline, trashOutline, timerOutline, repeatOutline, chevronForwardOutline,
  searchOutline, checkmark, addCircle,
} from 'ionicons/icons';
import MiniCalendarPicker from '../../../components/MiniCalendarPicker';
import GcalTimePicker from '../../../components/GcalTimePicker';
import ServiceTypeModal from '../../../components/ServiceTypeModal';
import ServiceOrderInline from '../../../components/ServiceOrderInline';
import { type ServiceOrderDraft } from '../../../components/ServiceOrderModal';
import { ServiceType, AdditionalService } from '@rsmaids/shared/types/services';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { callAdminApi } from '@rsmaids/shared/services/api/wixAdminApi';
import { getCachedContact, getCachedContactList } from '@/utils/contactCache';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { Contact, ContactListItem, ContactEmail, ContactPhone, ContactAddress } from '@rsmaids/shared/types/contacts';
import { BookingItem, Resource } from '@rsmaids/shared/types/bookings';
import type { Booking } from '@rsmaids/shared/types/bookings';
import { ServiceInfo } from '@rsmaids/shared/types/services';
import { useAuth } from '../../../contexts';
import { createServiceOrder, updateServiceOrder, getServiceOrder, confirmServiceOrder } from '../../../services/zohoServiceOrderService';
import styles from './CreateBookingDesktop.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../../i18n/dynamic';
import { intlLocale } from '../../../i18n/locale';

interface CreateBookingDesktopProps {
  onClose: () => void;
  bookingId?: string;
  contactId?: string;
  initialServiceType?: string;
}

const CreateBookingDesktop: React.FC<CreateBookingDesktopProps> = ({ onClose, bookingId, contactId, initialServiceType }) => {
  const { t } = useTranslation();
  const isEditing = bookingId && bookingId !== 'new';
  const { authState } = useAuth();
  const { user } = authState;

  // ── State (identical to BookingForm) ──────────────────────────────────────
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [contact, setContact] = useState<BookingItem['contact'] | null>(null);
  const [showContactSearch, setShowContactSearch] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [allContacts, setAllContacts] = useState<ContactListItem[]>([]);
  const contactSearchRef = useRef<HTMLInputElement>(null);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [contactAddresses, setContactAddresses] = useState<Contact['addresses']>([]);
  const [selectedBookingAddress, setSelectedBookingAddress] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [duration, setDuration] = useState<number>(0);
  const [repeatOption, setRepeatOption] = useState<string>("Doesn't repeat");
  const [serviceType, setServiceType] = useState<ServiceType | ''>(initialServiceType as ServiceType || '');
  const [serviceThumbnail, setServiceThumbnail] = useState<string>('');
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false);
  const [additionalServices, setAdditionalServices] = useState<AdditionalService[]>([]);
  const [isAddingAdditionalService, setIsAddingAdditionalService] = useState<boolean>(false);
  const [currentAdditionalServiceIndex, setCurrentAdditionalServiceIndex] = useState<number>(-1);
  const [resourceOption, setResourceOption] = useState<string>('');
  const [staffMembers, setStaffMembers] = useState<string[]>([]);
  const [showStaffPicker, setShowStaffPicker] = useState(false);
  const [staffPickerSlot, setStaffPickerSlot] = useState(0);
  const [allStaffMembers, setAllStaffMembers] = useState<import('@rsmaids/shared/types/bookings').StaffMember[]>([]);
  const [staffAvatarMap, setStaffAvatarMap] = useState<Map<string, string>>(new Map());
  const [staffFetchLoading, setStaffFetchLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState('');
  const [notes, setNotes] = useState<string>('');
  const [notesAdded, setNotesAdded] = useState<boolean>(false);
  const [price, setPrice] = useState<number>(0);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<ServiceInfo[]>([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showServiceOrderForm, setShowServiceOrderForm] = useState(false);
  const [soNumberError, setSoNumberError] = useState<string | undefined>();
  const [serviceOrderDraft, setServiceOrderDraft] = useState<ServiceOrderDraft | null>(null);
  const [taxAmount, setTaxAmount] = useState<number | null>(null);
  // Reset SO-derived price state whenever the form targets a different
  // booking — computed during render (React's "adjust state when a
  // dependency changes" pattern) rather than as synchronous setState calls
  // at the top of the mount-data effect below, which now only handles the
  // actual async contact/SO/booking loading.
  const [priceResetKey, setPriceResetKey] = useState<{ isEditing: boolean; bookingId: string | undefined }>({ isEditing: !!isEditing, bookingId });
  if (priceResetKey.isEditing !== !!isEditing || priceResetKey.bookingId !== bookingId) {
    setPriceResetKey({ isEditing: !!isEditing, bookingId });
    setServiceOrderDraft(null);
    setPrice(0);
    setTaxAmount(null);
  }

  // Load contact if contactId provided
  useEffect(() => {
    if (contactId) {
      const loadContact = async () => {
        try {
          const contactService = DataServiceFactory.getService();
          const loadedContact = await contactService.contacts.getContactById(contactId);
          if (loadedContact) {
            setContact({
              id: loadedContact.id,
              name: `${loadedContact.firstName || ''} ${loadedContact.lastName || ''}`.trim(),
              email: loadedContact.emails?.[0]?.email,
              phone: loadedContact.phones?.[0]?.phone,
              address: loadedContact.addresses?.length > 0 ? (() => {
                const primaryAddr = loadedContact.addresses.find((a: ContactAddress) => a.primary) || loadedContact.addresses[0];
                const addr = primaryAddr.address;
                return [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ');
              })() : '',
              avatarUrl: loadedContact.avatar,
            });
            setContactAddresses(loadedContact.addresses || []);
          }
        } catch (err) {
          console.error('Failed to load contact:', err);
        }
      };
      loadContact();
    }
  }, [contactId]);
  const [presentAlert] = useIonAlert();
  const [groupedResources, setGroupedResources] = useState<{ [category: string]: Resource[] }>({});
  const [allowedResourceTypeIds, setAllowedResourceTypeIds] = useState<string[]>([]);
  const [showDurationPicker, setShowDurationPicker] = useState(false);
  const [showRepeatPicker, setShowRepeatPicker] = useState(false);
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [showCustomAddressInput, setShowCustomAddressInput] = useState(false);
  const [customAddressValue, setCustomAddressValue] = useState('');

  const dataService = DataServiceFactory.getService();

  // ── Load cached contact list for inline search ────────────────────────────
  useEffect(() => {
    getCachedContactList(() => dataService.contacts.getAllContacts())
      .then(result => {
        setAllContacts(result.data.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
      })
      .catch(() => { /* ignore */ });
  }, [dataService.contacts]);

  useEffect(() => {
    if (showContactSearch) {
      setTimeout(() => contactSearchRef.current?.focus(), 50);
    }
  }, [showContactSearch]);

  const filteredContacts = allContacts.filter(c => {
    const q = contactSearch.toLowerCase();
    if (!q) return true;
    return (
      (c.displayName || '').toLowerCase().includes(q) ||
      (`${c.firstName} ${c.lastName}`).toLowerCase().includes(q) ||
      c.primaryEmail.toLowerCase().includes(q) ||
      c.primaryPhone.includes(q)
    );
  }).slice(0, 40);

  // ── Helpers (identical to BookingForm) ───────────────────────────────────
  const formatTimeValue = (timeInput: string | Date): string => {
    if (timeInput instanceof Date) {
      return `${timeInput.getHours().toString().padStart(2, '0')}:${timeInput.getMinutes().toString().padStart(2, '0')}`;
    }
    if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        const d = new Date(timeInput);
        return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
      }
      return timeInput.substring(0, 5);
    }
    return '';
  };

  const calculateDuration = (startTime: string | Date, endTime: string | Date): number => {
    return Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / 60000);
  };

  const createBookingContact = (c: Contact) => ({
    id: c.id,
    name: `${c.firstName} ${c.lastName}`.trim(),
    email: c.emails?.find((e: ContactEmail) => e.primary)?.email || c.emails?.[0]?.email || '',
    phone: c.phones?.find((p: ContactPhone) => p.primary)?.phone || c.phones?.[0]?.phone || '',
    address: c.addresses?.length > 0 ? (() => {
      const primaryAddr = c.addresses.find((a: ContactAddress) => a.primary) || c.addresses[0];
      const addr = primaryAddr.address;
      return [addr.addressLine1, addr.addressLine2, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ');
    })() : '',
    avatarUrl: c.avatar,
  });

  // ── Effects (identical to BookingForm) ────────────────────────────────────
  useEffect(() => {
    const loadContactData = async () => {
      const storedData = DataStorageService.getData<{ id: string; name?: string } | Contact | null>('selectedContact');
      if (storedData) {
        DataStorageService.removeData('selectedContact');
        const cacheKey = `contact-${storedData.id}`;
        const cached = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(`${cacheKey}-time`);
        const isCacheValid = cached && cacheTime && (Date.now() - parseInt(cacheTime)) < 5 * 60 * 1000;
        if (isCacheValid) {
          try {
            const fullContact = JSON.parse(cached);
            setContact(createBookingContact(fullContact));
            setContactAddresses(fullContact.addresses || []);
            return;
          } catch { /* continue */ }
        }
        if (!('firstName' in storedData) || !('emails' in storedData)) {
          try {
            const result = await getCachedContact(storedData.id, () => dataService.contacts.getContactById(storedData.id));
            if (result.data) {
              setContact(createBookingContact(result.data));
              setContactAddresses(result.data.addresses || []);
            }
          } catch {
            setContact({ id: storedData.id, name: storedData.name || '', email: '', phone: '', address: '', avatarUrl: undefined });
          }
        } else {
          setContact(createBookingContact(storedData as Contact));
          setContactAddresses((storedData as Contact).addresses || []);
        }
      }
    };

    if (isEditing && bookingId) {
      getServiceOrder(bookingId).then(existing => {
        if (!existing) return;
        const total = existing.total ?? 0;
        const taxAmt = (existing.line_items ?? []).reduce((sum, li) => {
          const lineAmt = li.quantity * li.rate;
          const discAmt = li.discount ? lineAmt * (li.discount / 100) : 0;
          const net = lineAmt - discAmt;
          const pct = li.tax_percentage ?? 0;
          return sum + (existing.is_inclusive_tax ? net - net / (1 + pct / 100) : net * (pct / 100));
        }, 0);
        const draft: ServiceOrderDraft = {
          soNumber: existing.salesorder_number ?? '',
          soDate: existing.date ?? new Date().toISOString().split('T')[0],
          paymentTerms: existing.payment_terms ?? 3,
          paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
          serviceMonth: '',
          isInclusiveTax: existing.is_inclusive_tax ?? true,
          pricelistId: existing.pricelist_id ?? '',
          lineItems: (existing.line_items ?? []).map(li => ({
            item_id: li.item_id || undefined,
            name: li.name || '',
            description: li.description || '',
            quantity: li.quantity || 1,
            rate: li.rate || 0,
            tax_id: li.tax_id || undefined,
            tax_name: li.tax_name || undefined,
            discount: li.discount || undefined,
            discountType: li.discount ? '%' : undefined,
          })),
          notes: existing.customer_notes ?? '',
          terms: existing.terms ?? '',
          total,
          taxAmount: taxAmt,
          action: 'confirm',
        };
        setServiceOrderDraft(draft);
        setPrice(total);
        setTaxAmount(taxAmt);
      }).catch(() => {});
    }

    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — this logic is entirely synchronous itself, but
    // the effect-setState rule flags any setState reachable directly at an
    // effect's top level.
    Promise.resolve().then(() => {
      const storedDate = DataStorageService.getData<string>('bookingSelectedDate');
      DataStorageService.removeData('bookingSelectedDate');

      if (isEditing && bookingId) {
        const storedBookingData = DataStorageService.getData<BookingItem>(`edit-booking-${bookingId}`);
        if (storedBookingData) {
          setContact(storedBookingData.contact);
          setServiceType(storedBookingData.serviceType);
          const startAsDate = storedBookingData.startTime ? new Date(storedBookingData.startTime) : new Date(storedBookingData.date);
          setSelectedDate(startAsDate);
          const timeValue = formatTimeValue(storedBookingData.startTime);
          if (timeValue) setSelectedTime(timeValue);
          let durationValue = storedBookingData.duration || 0;
          if (durationValue === 0 && storedBookingData.startTime && storedBookingData.endTime) {
            durationValue = calculateDuration(storedBookingData.startTime, storedBookingData.endTime);
          }
          setDuration(durationValue);
          setStaffMembers(Array.isArray(storedBookingData.staff) ? storedBookingData.staff : [storedBookingData.staff || '']);
          setResourceOption(storedBookingData.resource || '');
          setNotes(storedBookingData.notes || '');
          if (storedBookingData.notes) setNotesAdded(true);
          setPrice(storedBookingData.price || 0);
          setSelectedBookingAddress(storedBookingData.contact?.address || '');
        }
      } else if (storedDate) {
        const dateObj = new Date(storedDate);
        setSelectedDate(dateObj);
        setSelectedTime(formatTimeValue(dateObj));
      } else {
        setSelectedDate(null);
        setSelectedTime('');
      }

      loadContactData();
    });
  }, [bookingId, isEditing, dataService.contacts]);

  useEffect(() => {
    if (!isEditing || !bookingId) return;
    let cancelled = false;
    getServiceOrder(bookingId).then(existing => {
      if (cancelled || !existing) return;
      const lineItems = (existing.line_items ?? []).map(li => ({
        item_id: li.item_id || undefined,
        name: li.name || '',
        description: li.description || '',
        quantity: li.quantity || 1,
        rate: li.rate || 0,
        tax_id: li.tax_id || undefined,
        tax_name: li.tax_name || undefined,
        tax_percentage: li.tax_percentage || 0,
      }));
      const draft: ServiceOrderDraft = {
        soNumber: existing.salesorder_number ?? '',
        soDate: existing.date ?? new Date().toISOString().split('T')[0],
        paymentTerms: existing.payment_terms ?? 3,
        paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
        serviceMonth: '',
        isInclusiveTax: existing.is_inclusive_tax ?? true,
        pricelistId: existing.pricelist_id ?? '',
        lineItems,
        notes: existing.customer_notes ?? '',
        terms: existing.terms ?? '',
        total: existing.total ?? 0,
        taxAmount: 0,
        action: 'confirm',
      };
      setServiceOrderDraft(draft);
      setPrice(existing.total ?? 0);
      setTaxAmount(null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isEditing, bookingId]);

  // Recalculates the default price from duration/serviceType/additionalServices
  // whenever any of those change — computed during render (React's "adjust
  // state when a dependency changes" pattern) rather than as setState calls
  // inside an effect, since this is a pure derivation with no external
  // system involved. `price` still stays real state (not folded into a
  // useMemo) because it's also set directly elsewhere from async sources
  // (stored booking data, a loaded service order) that this calculation
  // must yield to, not overwrite.
  const [prevPriceCalcInputs, setPrevPriceCalcInputs] = useState({ serviceType, duration, additionalServices, serviceOrderDraft });
  if (
    prevPriceCalcInputs.serviceType !== serviceType ||
    prevPriceCalcInputs.duration !== duration ||
    prevPriceCalcInputs.additionalServices !== additionalServices ||
    prevPriceCalcInputs.serviceOrderDraft !== serviceOrderDraft
  ) {
    setPrevPriceCalcInputs({ serviceType, duration, additionalServices, serviceOrderDraft });
    if (!serviceOrderDraft) {
      if (duration === 0 || serviceType === 'Free Estimate') {
        setPrice(0);
      } else {
        let basePrice = 55;
        if (serviceType?.includes('Recurring')) basePrice = 50;
        else if (serviceType?.includes('Deep')) basePrice = 60;
        else if (serviceType?.includes('Move') || serviceType?.includes('Out')) basePrice = 70;
        let totalPrice = basePrice * (duration / 60);
        additionalServices.forEach(service => {
          let additionalCost = 15;
          if (service.type.includes('Deep')) additionalCost = 25;
          else if (service.type.includes('Move') || service.type.includes('Out')) additionalCost = 20;
          totalPrice += additionalCost;
        });
        setPrice(totalPrice);
      }
    }
  }

  useEffect(() => {
    return () => { DataStorageService.storeData('calendarShouldRefresh', true); };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetch = async () => {
      setServiceTypesLoading(true);
      try {
        const svc = DataServiceFactory.getService();
        const servicesData = await svc.getServiceTypes();
        await Promise.all(servicesData.map((s: { imageUrl?: string }) => new Promise<void>(resolve => {
          if (!s.imageUrl) return resolve();
          const img = new window.Image();
          img.onload = img.onerror = () => resolve();
          img.src = s.imageUrl;
        })));
        if (isMounted) setServiceTypeOptions(servicesData);
      } finally {
        if (isMounted) setServiceTypesLoading(false);
      }
    };
    fetch();
    return () => { isMounted = false; };
  }, []);

  // Set service thumbnail and (for new bookings) default duration when
  // service types are loaded and we have a serviceType — computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as setState calls inside effects, since both are pure
  // derivations from already-available props/state.
  const [prevThumbnailInputs, setPrevThumbnailInputs] = useState({ serviceType, serviceTypeOptions });
  if (prevThumbnailInputs.serviceType !== serviceType || prevThumbnailInputs.serviceTypeOptions !== serviceTypeOptions) {
    setPrevThumbnailInputs({ serviceType, serviceTypeOptions });
    if (serviceType && serviceTypeOptions.length > 0) {
      const norm = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
      const match = serviceTypeOptions.find(s => norm(s.type) === norm(serviceType as string));
      setServiceThumbnail(match?.imageUrl || '/assets/images/bucket.svg');
      if (!isEditing && duration === 0 && match?.defaultDuration && match.defaultDuration > 0) {
        setDuration(match.defaultDuration);
      }
    }
  }

  useEffect(() => {
    let isMounted = true;
    const fetch = async () => {
      if (!serviceType || serviceTypeOptions.length === 0) { setAllowedResourceTypeIds([]); return; }
      try {
        const normalize = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
        const matching = serviceTypeOptions.find(s => normalize(s.type) === normalize(serviceType as string));
        if (!matching?.serviceId) { setAllowedResourceTypeIds([]); return; }
        const result = await callAdminApi('getService', 'services', { id: matching.serviceId });
        const svc = result as { serviceResources?: Array<{ _id?: string; resourceType?: { _id?: string } }> };
        const ids = (svc.serviceResources || []).map(item => item.resourceType?._id || item._id).filter((id): id is string => Boolean(id));
        if (isMounted) setAllowedResourceTypeIds(ids);
      } catch {
        if (isMounted) setAllowedResourceTypeIds([]);
      }
    };
    fetch();
    return () => { isMounted = false; };
  }, [serviceType, serviceTypeOptions]);

  useEffect(() => {
    let isMounted = true;
    const fetch = async () => {
      setResourcesLoading(true);
      try {
        const svc = DataServiceFactory.getService();
        const grouped = await svc.resources.getResourcesGroupedByCategory();
        // 'Unassigned' is the service's fallback bucket for resources with no type — not bookable here.
        const { Unassigned: _unassigned, ...bookable } = grouped; // i18n-ignore: data key from the resources service
        if (isMounted) setGroupedResources(bookable);
      } catch {
        if (isMounted) setGroupedResources({});
      } finally {
        if (isMounted) setResourcesLoading(false);
      }
    };
    fetch();
    return () => { isMounted = false; };
  }, []);

  // Fetch staff members once on mount
  useEffect(() => {
    let isMounted = true;
    const fetch = async () => {
      setStaffFetchLoading(true);
      try {
        const svc = DataServiceFactory.getService();
        const staff = await svc.staff.getAllStaffMembers();
        if (!isMounted) return;
        setAllStaffMembers(staff);
        const avatarMap = new Map<string, string>();
        const contactIds = staff
          .map(m => (m.associatedWixIdentity as Record<string, unknown> | undefined)
            ?.identificationData as Record<string, unknown> | undefined)
          .map(id => id?.contactId as string | undefined)
          .filter((id): id is string => Boolean(id));
        if (contactIds.length) {
          const contacts = await Promise.all(
            contactIds.map(id => svc.contacts.getContactById(id).catch(() => null))
          );
          contacts.forEach(c => { if (c?.avatar) avatarMap.set(c._id as string, c.avatar); });
          staff.forEach(m => {
            const cId = ((m.associatedWixIdentity as Record<string, unknown> | undefined)
              ?.identificationData as Record<string, unknown> | undefined)?.contactId as string | undefined;
            if (cId && avatarMap.has(cId)) {
              avatarMap.set(m._id, avatarMap.get(cId)!);
            } else {
              const img = (m.mainMedia as Record<string, unknown> | undefined)?.image;
              if (img && typeof img === 'string') avatarMap.set(m._id, img);
            }
          });
        }
        if (isMounted) setStaffAvatarMap(avatarMap);
      } catch {
        if (isMounted) setAllStaffMembers([]);
      } finally {
        if (isMounted) setStaffFetchLoading(false);
      }
    };
    fetch();
    return () => { isMounted = false; };
  }, []);

  // ── Handlers (identical to BookingForm) ───────────────────────────────────
  const handleContactSelect = async (selectedContact: ContactListItem) => {
    setShowContactSearch(false);
    setContactSearch('');
    try {
      const result = await getCachedContact(selectedContact.id, () => dataService.contacts.getContactById(selectedContact.id));
      if (result.data) {
        setContactAddresses(result.data.addresses || []);
        const bookingContact: BookingItem['contact'] = createBookingContact(result.data);
        setContact(bookingContact);
        setSelectedBookingAddress(bookingContact.address);
      }
    } catch (error) {
      console.error('Error fetching contact data:', error);
    }
  };

  const handleCustomAddressSubmit = () => {
    if (customAddressValue.trim()) {
      setSelectedBookingAddress(customAddressValue.trim());
      if (contact) setContact({ ...contact, address: customAddressValue.trim() });
      setShowCustomAddressInput(false);
      setCustomAddressValue('');
      setShowAddressPicker(false);
    }
  };

  const handleTimeSelect = (timeString: string) => {
    setSelectedTime(timeString);
    if (selectedDate) {
      const [hours, minutes] = timeString.split(':').map(Number);
      const newDate = new Date(selectedDate);
      newDate.setHours(hours, minutes, 0, 0);
      setSelectedDate(newDate);
    }
    setShowTimePicker(false);
  };

  const handleServiceTypeSelectWithIndex = (type: ServiceType, imageUrl: string) => {
    const validImageUrl = imageUrl || '/assets/images/bucket.svg';
    if (isAddingAdditionalService) {
      if (currentAdditionalServiceIndex !== -1) {
        const updated = [...additionalServices];
        updated[currentAdditionalServiceIndex] = { type, thumbnail: validImageUrl };
        setAdditionalServices(updated);
        setCurrentAdditionalServiceIndex(-1);
      } else {
        setAdditionalServices([...additionalServices, { type, thumbnail: validImageUrl }]);
      }
      setIsAddingAdditionalService(false);
    } else {
      setServiceType(type);
      setServiceThumbnail(validImageUrl);
      const normalize = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
      const matched = serviceTypeOptions.find(s => normalize(s.type) === normalize(type));
      if (matched?.defaultDuration && matched.defaultDuration > 0) setDuration(matched.defaultDuration);
      if (serviceOrderDraft) { setIsServiceTypeModalOpen(false); return; }
      setServiceOrderDraft(null);
      if (isEditing && bookingId) {
        setIsSaving(true);
        getServiceOrder(bookingId).then(existing => {
          if (existing) {
            presentAlert({ header: t('desktop.pages.bookings.CreateBookingDesktop.serviceOrderExists'), message: t('desktop.pages.bookings.CreateBookingDesktop.aServiceOrderAlreadyExistsAnd', { salesorder_number: existing.salesorder_number }), buttons: ['OK'] });
          } else {
            setShowServiceOrderForm(true);
          }
        }).catch(() => setShowServiceOrderForm(true)).finally(() => setIsSaving(false));
      } else {
        setShowServiceOrderForm(true);
      }
    }
    setIsServiceTypeModalOpen(false);
  };

  const openServiceOrderModal = async () => {
    if (isEditing && bookingId && !serviceOrderDraft) {
      setIsSaving(true);
      try {
        const existing = await getServiceOrder(bookingId);
        if (existing) {
          const draft: ServiceOrderDraft = {
            soNumber: existing.salesorder_number ?? '',
            soDate: existing.date ?? new Date().toISOString().split('T')[0],
            paymentTerms: existing.payment_terms ?? 3,
            paymentTermsLabel: existing.payment_terms_label ?? 'Net 3',
            serviceMonth: '',
            isInclusiveTax: existing.is_inclusive_tax ?? true,
            pricelistId: existing.pricelist_id ?? '',
            lineItems: (existing.line_items ?? []).map(li => ({
              item_id: li.item_id || undefined,
              name: li.name || '',
              description: li.description || '',
              quantity: li.quantity || 1,
              rate: li.rate || 0,
              tax_id: li.tax_id || undefined,
              tax_name: li.tax_name || undefined,
              discount: li.discount || undefined,
              discountType: (li.discount ? (li.discount_type === 'percentage' ? '%' : '$') : undefined),
            })),
            notes: existing.customer_notes ?? '',
            terms: existing.terms ?? '',
            total: existing.total ?? 0,
            taxAmount: (() => {
              return (existing.line_items ?? []).reduce((sum, li) => {
                const lineAmt = li.quantity * li.rate;
                const discType = li.discount_type === 'percentage' ? '%' : '$';
                const discAmt = li.discount ? (discType === '%' ? lineAmt * (li.discount / 100) : li.discount) : 0;
                const net = lineAmt - discAmt;
                const pct = li.tax_percentage ?? 0;
                return sum + (existing.is_inclusive_tax ? net - net / (1 + pct / 100) : net * (pct / 100));
              }, 0);
            })(),
            action: 'confirm',
          };
          setServiceOrderDraft(draft);
          setPrice(draft.total ?? 0);
          setTaxAmount(draft.taxAmount ?? null);
        }
      } catch { /* open modal with no pre-population */ }
      finally { setIsSaving(false); }
    }
    setShowServiceOrderForm(true);
  };

  const handleServiceOrderConfirm = (draft: ServiceOrderDraft) => {
    setServiceOrderDraft(draft);
    setPrice(draft.total);
    setTaxAmount(draft.taxAmount);
    const firstItemName = draft.lineItems?.[0]?.name?.toLowerCase() ?? '';
    if (firstItemName) {
      const keywordMap: Array<{ keywords: string[]; service: ServiceType }> = [
        { keywords: ['deep clean', 'deep-clean'], service: 'Deep Cleaning' },
        { keywords: ['move out', 'move-out', 'moveout'], service: 'Move Out Cleaning' },
        { keywords: ['move in', 'move-in', 'movein'], service: 'Move Out Cleaning' },
        { keywords: ['post construction', 'post-construction'], service: 'Post Construction Cleaning' },
        { keywords: ['window'], service: 'Window Cleaning' },
        { keywords: ['oven'], service: 'Oven Cleaning' },
        { keywords: ['refrigerator', 'fridge'], service: 'Refrigerator Cleaning' },
        { keywords: ['airbnb', 'air bnb', 'vacation'], service: 'AirBnb Cleaning Service' },
        { keywords: ['commercial'], service: 'Commercial Cleaning' },
        { keywords: ['office'], service: 'Office Cleaning' },
        { keywords: ['estimate'], service: 'Free Estimate' },
        { keywords: ['recurring', 'regular', 'standard'], service: 'Recurring Cleaning' },
      ];
      const entry = keywordMap.find(e => e.keywords.some(k => firstItemName.includes(k)));
      if (entry?.service) {
        const norm = (s: string) => s.toLowerCase().replace(/[-\s]+/g, ' ').trim();
        const matched = serviceTypeOptions.find(s => norm(s.type) === norm(entry.service));
        setServiceType(entry.service);
        if (matched?.defaultDuration && matched.defaultDuration > 0) setDuration(matched.defaultDuration);
        setServiceThumbnail(matched?.imageUrl || '/assets/images/bucket.svg');
      }
    }
  };

  const handleRemoveAdditionalService = (index: number) => {
    setAdditionalServices(prev => prev.filter((_, i) => i !== index));
  };

  const handleStaffMemberSelect = (staff: string) => {
    if (staffPickerSlot === -1) {
      setStaffMembers(prev => [...prev, staff]);
    } else {
      setStaffMembers(prev => { const updated = [...prev]; updated[staffPickerSlot] = staff; return updated; });
    }
    setShowStaffPicker(false);
    setStaffSearch('');
  };

  const handleAddStaffMember = () => {
    setStaffPickerSlot(-1);
    setStaffSearch('');
    setShowStaffPicker(true);
  };

  const handleRemoveStaffMember = (index: number) => {
    setStaffMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateBooking = async () => {
    if (!contact || !serviceType || !resourceOption || staffMembers.length === 0 || !selectedTime || duration === 0 || !selectedDate) return;
    setIsSaving(true);
    try {
      const _y = selectedDate.getFullYear();
      const _m = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const _d = String(selectedDate.getDate()).padStart(2, '0');
      const localDateStr = `${_y}-${_m}-${_d}`;
      const startDateTime = new Date(`${localDateStr}T${selectedTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
      const bookingData = {
        contactId: contact.id,
        title: `${serviceType} - ${contact.name}`,
        serviceType: serviceType as ServiceType,
        startTime: startDateTime,
        endTime: endDateTime,
        date: localDateStr,
        location: selectedBookingAddress || contact.address,
        notes,
        staff: staffMembers.join(', '),
        resource: resourceOption,
        resourceTypeIds: allowedResourceTypeIds,
        duration,
        price,
        status: 'confirmed' as const,
        contact,
        isPaid: false,
        isCompleted: false,
        frequency: 'one-time' as const,
      };

      if (isEditing && bookingId) {
        const existingBooking = await dataService.bookings.getBookingById(bookingId);
        if (existingBooking) {
          const updatedBooking: Booking = { ...existingBooking, ...bookingData, id: existingBooking.id };
          // Free Estimate is never a billable item — no Zoho SO for it
          if (serviceType !== 'Free Estimate') {
            try {
              await updateServiceOrder({
                bookingId,
                contactName: contact?.name || 'Guest',
                contactEmail: contact?.email || undefined,
                serviceName: serviceType || 'Cleaning Service',
                startDate: startDateTime.toISOString(),
                endDate: endDateTime.toISOString(),
                duration: duration || undefined,
                resource: resourceOption || undefined,
                staffMembers: staffMembers.length ? staffMembers : undefined,
                address: selectedBookingAddress || contact?.address || undefined,
                ...(serviceOrderDraft ? {
                  draftLineItems: serviceOrderDraft.lineItems,
                  soNumber: serviceOrderDraft.soNumber,
                  soDate: serviceOrderDraft.soDate,
                  paymentTerms: serviceOrderDraft.paymentTerms,
                  paymentTermsLabel: serviceOrderDraft.paymentTermsLabel,
                  serviceMonth: serviceOrderDraft.serviceMonth,
                  serviceMonthCustomFieldId: serviceOrderDraft.serviceMonthCustomFieldId,
                  isInclusiveTax: serviceOrderDraft.isInclusiveTax,
                  pricelistId: serviceOrderDraft.pricelistId,
                  soStatus: serviceOrderDraft.action === 'confirm' ? 'confirmed' : 'draft',
                  customerNotes: serviceOrderDraft.notes || undefined,
                  terms: serviceOrderDraft.terms,
                  notes: serviceOrderDraft.notes || undefined,
                  price: serviceOrderDraft.total,
                } : {}),
              });
            } catch (zohoErr) {
              console.error('[CreateBookingDesktop] Zoho SO update failed:', zohoErr);
              const continueAnyway = await new Promise<boolean>(resolve => {
                presentAlert({
                  header: t('desktop.pages.bookings.CreateBookingDesktop.serviceOrderError'),
                  message: t('desktop.pages.bookings.CreateBookingDesktop.theServiceOrderCouldNotBe'),
                  cssClass: 'logout-alert',
                  buttons: [
                    { text: t('desktop.pages.bookings.CreateBookingDesktop.cancel'), role: 'cancel', handler: () => resolve(false) },
                    { text: t('desktop.pages.bookings.CreateBookingDesktop.continueAnyway'), handler: () => resolve(true) },
                  ],
                });
              });
              if (!continueAnyway) return;
            }
          }
          await dataService.bookings.updateBooking(updatedBooking);
          DataStorageService.storeData('booking-refresh-trigger', Date.now().toString());
          DataStorageService.notifyBookingChanged();
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } else {
        const result = await dataService.bookings.createBookingWithAvailabilityCheck(bookingData, {
          userRole: user?.role as 'owner' | 'admin' | 'manager' | 'user',
        });
        if (result.error) throw new Error(result.error);
        const resultObj = result as Record<string, unknown>;
        const bookingObj = resultObj['booking'] as Record<string, unknown> | undefined;
        const innerBooking = (bookingObj?.['booking'] ?? bookingObj) as Record<string, unknown> | undefined;
        const wixBookingId: string = (innerBooking?.['_id'] ?? innerBooking?.['id'] ?? bookingObj?.['_id'] ?? resultObj['bookingId'] ?? resultObj['id'] ?? '') as string;
        // Free Estimate is never a billable item — no Zoho SO for it
        if (wixBookingId && serviceType !== 'Free Estimate') {
          createServiceOrder({
            bookingId: wixBookingId,
            contactName: contact.name || 'Guest',
            contactEmail: contact.email || undefined,
            contactPhone: contact.phone || undefined,
            serviceName: serviceType || 'Cleaning Service',
            startDate: startDateTime.toISOString(),
            endDate: endDateTime.toISOString(),
            duration: duration || undefined,
            resource: resourceOption || undefined,
            staffMembers: staffMembers.length ? staffMembers : undefined,
            address: selectedBookingAddress || contact.address || undefined,
            // Only the service order's own notes — the booking's staff notes are Wix-only.
            notes: serviceOrderDraft?.notes || undefined,
            ...(serviceOrderDraft ? {
              draftLineItems: serviceOrderDraft.lineItems,
              soNumber: serviceOrderDraft.soNumber,
              soDate: serviceOrderDraft.soDate,
              paymentTerms: serviceOrderDraft.paymentTerms,
              paymentTermsLabel: serviceOrderDraft.paymentTermsLabel,
              serviceMonth: serviceOrderDraft.serviceMonth,
              serviceMonthCustomFieldId: serviceOrderDraft.serviceMonthCustomFieldId,
              isInclusiveTax: serviceOrderDraft.isInclusiveTax,
              pricelistId: serviceOrderDraft.pricelistId,
              soStatus: serviceOrderDraft.action === 'confirm' ? 'confirmed' : 'draft',
              customerNotes: serviceOrderDraft.notes || undefined,
              terms: serviceOrderDraft.terms,
              price: serviceOrderDraft.total,
            } : {}),
          })
            .then(() => {
              // The ServiceOrderModal has no UI path that ever produces
              // action:'confirm' (only "Save as Draft" and "Save & Send",
              // wired to 'draft'/'send') — every SO created through it
              // landed as a Zoho draft regardless of what the admin picked,
              // even though this booking is always confirmed immediately
              // (createBookingWithAvailabilityCheck always calls
              // confirmBooking internally). Confirm the SO here instead,
              // decoupled from the modal's own action value, so the SO
              // reflects "booking was actually confirmed" rather than
              // whichever button happened to exist in that modal.
              return confirmServiceOrder(wixBookingId);
            })
            .catch(err => console.error('[CreateBookingDesktop] Zoho SO create/confirm failed (non-fatal):', err));
        }
        DataStorageService.storeData('booking-refresh-trigger', Date.now().toString());
        DataStorageService.notifyBookingChanged();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      onClose();
    } catch (error) {
      console.error('Error saving booking:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const canSubmit = !!(contact && serviceType && resourceOption && staffMembers.length > 0 && selectedTime && duration > 0 && selectedDate && !resourcesLoading && !isSaving);

  const formatDisplayDate = (date: Date | null) =>
    date ? date.toLocaleDateString(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : t('desktop.pages.bookings.CreateBookingDesktop.selectDate');

  const formatDisplayTime = (time: string) =>
    time?.length >= 4 && time.includes(':')
      ? new Date(`2000-01-01T${time}`).toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })
      : t('desktop.pages.bookings.CreateBookingDesktop.selectTime');

  const formatDisplayDuration = (mins: number) =>
    mins > 0 ? `${Math.floor(mins / 60)}h${mins % 60 > 0 ? ` ${mins % 60}m` : ''}` : t('desktop.pages.bookings.CreateBookingDesktop.selectDuration');

  const serviceTotal = price - (taxAmount ?? price * 0.15);
  const taxTotal = taxAmount ?? price * 0.15;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <IonPage className={styles.page}>
      <IonHeader className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <div className={styles.topBarLeft} slot="start">
            <h1 className={styles.pageTitle}>{isEditing ? t('desktop.pages.bookings.CreateBookingDesktop.editBooking') : t('desktop.pages.bookings.CreateBookingDesktop.newBooking')}</h1>
          </div>
          <div className={styles.topBarRight} slot="end">
            {price > 0 && (
              <div className={styles.priceSummary}>
                <span className={styles.priceChunk}><span className={styles.priceLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.service')}</span><span className={styles.priceVal}>${serviceTotal.toFixed(2)}</span></span>
                <span className={styles.priceSep} />
                <span className={styles.priceChunk}><span className={styles.priceLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.tax')}</span><span className={styles.priceVal}>${taxTotal.toFixed(2)}</span></span>
                <span className={styles.priceSep} />
                <span className={styles.priceChunk}><span className={styles.priceLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.total')}</span><span className={`${styles.priceVal} ${styles.priceTotal}`}>${price.toFixed(2)}</span></span>
              </div>
            )}
            <IonButton fill="outline" size="small" className={styles.cancelBtn} onClick={onClose}>{t('desktop.pages.bookings.CreateBookingDesktop.cancel')}
            </IonButton>
            <IonButton
              fill="solid"
              size="small"
              className={styles.primaryBtn}
              disabled={!canSubmit}
              onClick={handleCreateBooking}
            >
              {isSaving && <IonSpinner name="bubbles" color="primary" style={{ width: 14, height: 14, marginRight: 6 }} />}
              {isEditing ? t('desktop.pages.bookings.CreateBookingDesktop.saveChanges') : t('desktop.pages.bookings.CreateBookingDesktop.scheduleBooking')}
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.content}>
        <div className={styles.formBody}>

          {/* ── Left Column ──────────────────────────────────────────────── */}
          <div className={styles.leftCol}>

            {/* Contact Card */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={personOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.contact')}
              </div>
              {contact && !showContactSearch ? (
                <>
                  <div className={styles.contactDisplay}>
                    <div className={styles.contactAvatar}>
                      <img
                        src={proxyImage(contact.avatarUrl ?? '') || getDefaultAvatar(contact.name)}
                        alt={contact.name}
                        onError={e => { (e.target as HTMLImageElement).src = getDefaultAvatar(contact.name); }}
                      />
                    </div>
                    <div className={styles.contactInfo}>
                      <div className={styles.contactName}>{contact.name}</div>
                      {contact.email && <div className={styles.contactDetail}>{contact.email}</div>}
                      {contact.phone && <div className={styles.contactDetail}>{contact.phone}</div>}
                    </div>
                    <button className={styles.changeBtn} onClick={() => { setShowContactSearch(true); setContactSearch(''); }}>{t('desktop.pages.bookings.CreateBookingDesktop.change')}
                    </button>
                  </div>

                  <div className={styles.addressPickerWrap}>
                    <button
                      className={styles.fieldRow}
                      onClick={() => { setShowAddressPicker(p => !p); setShowCustomAddressInput(false); }}
                    >
                      <IonIcon icon={locationOutline} className={styles.fieldIcon} />
                      <div className={styles.fieldContent}>
                        <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.serviceAddress')}</div>
                        <div className={`${styles.fieldValue} ${!selectedBookingAddress && !contact.address ? styles.fieldPlaceholder : ''}`}>
                          {selectedBookingAddress || contact.address || t('desktop.pages.bookings.CreateBookingDesktop.clickToSelectAddress')}
                        </div>
                      </div>
                      <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                    </button>

                    {showAddressPicker && (
                      <>
                        <div
                          className={styles.addressBackdrop}
                          onClick={() => { setShowAddressPicker(false); setShowCustomAddressInput(false); setCustomAddressValue(''); }}
                        />
                        <div className={styles.addressDropdown}>
                          {!showCustomAddressInput ? (
                            <>
                              {contactAddresses.map((addr, i) => {
                                const formatted = [
                                  addr.address.addressLine1, addr.address.addressLine2,
                                  addr.address.city, addr.address.state,
                                  addr.address.postalCode, addr.address.country,
                                ].filter(Boolean).join(', ');
                                const isSelected = selectedBookingAddress === formatted;
                                return (
                                  <button
                                    key={i}
                                    className={`${styles.addressOption} ${isSelected ? styles.addressOptionSelected : ''}`}
                                    onClick={() => {
                                      setSelectedBookingAddress(formatted);
                                      if (contact) setContact({ ...contact, address: formatted });
                                      setShowAddressPicker(false);
                                    }}
                                  >
                                    <IonIcon icon={locationOutline} className={styles.addrIcon} />
                                    <span className={styles.addrText}>{formatted}</span>
                                    {isSelected && <IonIcon icon={checkmark} className={styles.addrCheck} />}
                                  </button>
                                );
                              })}
                              <button
                                className={styles.addressCustomBtn}
                                onClick={() => { setShowCustomAddressInput(true); setCustomAddressValue(''); }}
                              >
                                <IonIcon icon={addOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.enterCustomAddress')}
                              </button>
                            </>
                          ) : (
                            <div className={styles.customAddressForm}>
                              <div className={styles.customAddressLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.customAddress')}</div>
                              <input
                                autoFocus
                                className={styles.customAddressInput}
                                value={customAddressValue}
                                onChange={e => setCustomAddressValue(e.target.value)}
                                placeholder={t('desktop.pages.bookings.CreateBookingDesktop.enterTheFullAddress')}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleCustomAddressSubmit();
                                  if (e.key === 'Escape') setShowCustomAddressInput(false);
                                }}
                              />
                              <div className={styles.customAddressActions}>
                                <button className={styles.addrBackBtn} onClick={() => setShowCustomAddressInput(false)}>{t('desktop.pages.bookings.CreateBookingDesktop.back')}
                                </button>
                                <button
                                  className={styles.addrSubmitBtn}
                                  disabled={!customAddressValue.trim()}
                                  onClick={handleCustomAddressSubmit}
                                >{t('desktop.pages.bookings.CreateBookingDesktop.useAddress')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : showContactSearch ? (
                <div className={styles.contactSearchWrap}>
                  <div className={styles.contactSearchBox}>
                    <IonIcon icon={searchOutline} className={styles.searchIcon} />
                    <input
                      ref={contactSearchRef}
                      className={styles.searchInput}
                      placeholder={t('desktop.pages.bookings.CreateBookingDesktop.searchByNameEmailOrPhone')}
                      value={contactSearch}
                      onChange={e => setContactSearch(e.target.value)}
                    />
                    <button className={styles.searchClearBtn} onClick={() => { setShowContactSearch(false); setContactSearch(''); }}>
                      <IonIcon icon={closeOutline} />
                    </button>
                  </div>
                  {filteredContacts.length > 0 && (
                    <div className={styles.contactResults}>
                      {filteredContacts.map(c => (
                        <button
                          key={c.id}
                          className={styles.contactResultItem}
                          onClick={() => handleContactSelect(c)}
                        >
                          <div className={styles.resultInitials}>
                            <img
                              src={proxyImage(c.avatar ?? '') || getDefaultAvatar(c.displayName || `${c.firstName} ${c.lastName}`.trim())}
                              alt=""
                              style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                            />
                          </div>
                          <div className={styles.resultInfo}>
                            <div className={styles.resultName}>{c.displayName || `${c.firstName} ${c.lastName}`.trim()}</div>
                            {(c.primaryEmail || c.primaryPhone) && (
                              <div className={styles.resultSub}>{c.primaryEmail || c.primaryPhone}</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {contactSearch.length > 0 && filteredContacts.length === 0 && (
                    <div className={styles.contactEmpty}>{t('desktop.pages.bookings.CreateBookingDesktop.noContactsFound')}</div>
                  )}
                </div>
              ) : (
                <button
                  className={styles.selectContactBtn}
                  onClick={() => { setShowContactSearch(true); setContactSearch(''); }}
                >
                  <IonIcon icon={personOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.selectContact')}
                </button>
              )}
            </div>

            {/* Schedule Card */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={calendarOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.schedule')}
              </div>

              {/* Date row + floating calendar */}
              <div className={styles.pickerWrap}>
                <button className={styles.fieldRow} onClick={() => { setShowDatePicker(p => !p); setShowTimePicker(false); }}>
                  <IonIcon icon={calendarOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.date')}</div>
                    <div className={`${styles.fieldValue} ${!selectedDate ? styles.fieldPlaceholder : ''}`}>
                      {formatDisplayDate(selectedDate)}
                    </div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showDatePicker && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => setShowDatePicker(false)} />
                    <div className={styles.calendarDropdown}>
                      <MiniCalendarPicker
                        desktop
                        value={selectedDate}
                        onSelect={date => {
                          const newDate = new Date(date);
                          if (selectedTime) {
                            const [h, m] = selectedTime.split(':').map(Number);
                            newDate.setHours(h, m, 0, 0);
                          }
                          setSelectedDate(newDate);
                          setShowDatePicker(false);
                        }}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Time row + floating GCal clock */}
              <div className={styles.pickerWrap}>
                <button className={styles.fieldRow} onClick={() => { setShowTimePicker(p => !p); setShowDatePicker(false); }}>
                  <IonIcon icon={timeOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.startTime')}</div>
                    <div className={`${styles.fieldValue} ${!selectedTime ? styles.fieldPlaceholder : ''}`}>
                      {formatDisplayTime(selectedTime)}
                    </div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showTimePicker && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => setShowTimePicker(false)} />
                    <div className={styles.timeDropdown}>
                      <GcalTimePicker
                        desktop
                        value={selectedTime || '09:00'}
                        onSelect={time => { handleTimeSelect(time); setShowTimePicker(false); }}
                        onClose={() => setShowTimePicker(false)}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Duration row + floating picker */}
              <div className={styles.pickerWrap}>
                <button className={styles.fieldRow} onClick={() => { setShowDurationPicker(p => !p); setShowRepeatPicker(false); }}>
                  <IonIcon icon={timerOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.duration')}</div>
                    <div className={`${styles.fieldValue} ${duration === 0 ? styles.fieldPlaceholder : ''}`}>
                      {formatDisplayDuration(duration)}
                    </div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showDurationPicker && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => setShowDurationPicker(false)} />
                    <div className={styles.listDropdown}>
                      {[15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180,
                        195, 210, 225, 240, 255, 270, 285, 300, 315, 330, 345, 360].map(val => {
                        const h = Math.floor(val / 60);
                        const m = val % 60;
                        const label = `${h}h${m > 0 ? ` ${m}m` : ''}`;
                        return (
                          <button
                            key={val}
                            className={`${styles.listOption} ${duration === val ? styles.listOptionSelected : ''}`}
                            onClick={() => { setDuration(val); setShowDurationPicker(false); }}
                          >
                            {label}
                            {duration === val && <IonIcon icon={checkmark} className={styles.optionCheck} />}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Repeat row + floating picker */}
              <div className={styles.pickerWrap}>
                <button className={styles.fieldRow} onClick={() => { setShowRepeatPicker(p => !p); setShowDurationPicker(false); }}>
                  <IonIcon icon={repeatOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.repeats')}</div>
                    <div className={styles.fieldValue}>{repeatOption}</div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showRepeatPicker && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => setShowRepeatPicker(false)} />
                    <div className={styles.listDropdown}>
                      {["Doesn't repeat", "Weekly", "Bi-weekly", "Every 3 weeks", "Every 4 weeks"].map(val => (
                        <button
                          key={val}
                          className={`${styles.listOption} ${repeatOption === val ? styles.listOptionSelected : ''}`}
                          onClick={() => { setRepeatOption(val); setShowRepeatPicker(false); }}
                        >
                          {val}
                          {repeatOption === val && <IonIcon icon={checkmark} className={styles.optionCheck} />}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

          </div>

          {/* ── Right Column ─────────────────────────────────────────────── */}
          <div className={styles.rightCol}>

            {/* Service selection card */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={cubeOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.serviceOrder')}
              </div>

              {!serviceType ? (
                <button
                  className={styles.selectServiceBtn}
                  onClick={() => { setIsAddingAdditionalService(false); void openServiceOrderModal(); }}
                >
                  <IonIcon icon={addCircle} />{t('desktop.pages.bookings.CreateBookingDesktop.chooseServiceConfigureOrder')}
                </button>
              ) : (
                <div className={styles.selectedService}>
                  <div className={styles.serviceThumbnailWrap}>
                    <img src={serviceThumbnail || '/assets/images/bucket.svg'} alt={serviceType as string} className={styles.serviceThumbnailImg} />
                  </div>
                  <span className={styles.serviceLabel}>{translateDynamic(serviceType)}</span>
                  <button
                    className={styles.changeBtn}
                    onClick={() => { setIsAddingAdditionalService(false); void openServiceOrderModal(); }}
                  >{t('desktop.pages.bookings.CreateBookingDesktop.change')}
                  </button>
                </div>
              )}

              {additionalServices.map((service, index) => (
                <div key={index} className={styles.selectedService}>
                  <div className={styles.serviceThumbnailWrap}>
                    <img src={service.thumbnail} alt={service.type} className={styles.serviceThumbnailImg} />
                  </div>
                  <span className={styles.serviceLabel}>{service.type}</span>
                  <button className={styles.removeBtn} onClick={() => handleRemoveAdditionalService(index)}>
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
              ))}

              {serviceType && (
                <button
                  className={styles.addLineBtn}
                  onClick={() => { setIsAddingAdditionalService(true); setCurrentAdditionalServiceIndex(-1); setIsServiceTypeModalOpen(true); }}
                >
                  <IonIcon icon={addOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.addAnotherService')}
                </button>
              )}
            </div>

            {/* Service Order inline form — shown when entering or editing order */}
            {showServiceOrderForm && (
              <div className={styles.card}>
                <div className={styles.cardTitle}>
                  <IonIcon icon={cubeOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.serviceOrder2')}
                </div>
                <ServiceOrderInline
                  customerName={contact?.name || ''}
                  contactId={contact?.id || ''}
                  bookingId={isEditing ? bookingId : ''}
                  soNumberError={soNumberError}
                  initialDraft={serviceOrderDraft}
                  defaultDate={selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : undefined}
                  onConfirm={draft => {
                    handleServiceOrderConfirm(draft);
                    setShowServiceOrderForm(false);
                    setSoNumberError(undefined);
                  }}
                />
              </div>
            )}

            {/* Service order confirmed summary */}
            {!showServiceOrderForm && serviceOrderDraft && (
              <div className={styles.card}>
                <div className={styles.soSummaryHeader}>
                  <span className={styles.cardTitle} style={{ marginBottom: 0 }}>{t('desktop.pages.bookings.CreateBookingDesktop.serviceOrder2')}</span>
                  <button className={styles.changeBtn} onClick={() => setShowServiceOrderForm(true)}>{t('desktop.pages.bookings.CreateBookingDesktop.editOrder')}
                  </button>
                </div>
                <div className={styles.soSummaryRow}>
                  <span className={styles.soSummaryKey}>{t('desktop.pages.bookings.CreateBookingDesktop.date')}</span>
                  <span className={styles.soSummaryVal}>{serviceOrderDraft.soDate}</span>
                </div>
                {serviceOrderDraft.serviceMonth && (
                  <div className={styles.soSummaryRow}>
                    <span className={styles.soSummaryKey}>{t('desktop.pages.bookings.CreateBookingDesktop.month')}</span>
                    <span className={styles.soSummaryVal}>{serviceOrderDraft.serviceMonth}</span>
                  </div>
                )}
                <div className={styles.soSummaryRow}>
                  <span className={styles.soSummaryKey}>{t('desktop.pages.bookings.CreateBookingDesktop.items')}</span>
                  <span className={styles.soSummaryVal}>{t('desktop.pages.bookings.CreateBookingDesktop.lineItem', { length: serviceOrderDraft.lineItems.length })}{serviceOrderDraft.lineItems.length !== 1 ? 's' : ''}</span>
                </div>
                <div className={`${styles.soSummaryRow} ${styles.soSummaryTotal}`}>
                  <span className={styles.soSummaryKey}>{t('desktop.pages.bookings.CreateBookingDesktop.total')}</span>
                  <span className={styles.soSummaryVal}>${serviceOrderDraft.total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Assignment Card */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <IonIcon icon={peopleOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.assignment')}
              </div>

              {/* Resource floating picker */}
              <div className={styles.pickerWrap}>
                <button className={styles.fieldRow} onClick={() => { setShowResourcePicker(p => !p); setShowStaffPicker(false); }}>
                  <IonIcon icon={constructOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.resource')}</div>
                    <div className={`${styles.fieldValue} ${!resourceOption ? styles.fieldPlaceholder : ''}`}>
                      {resourceOption || (resourcesLoading ? t('desktop.pages.bookings.CreateBookingDesktop.loading') : t('desktop.pages.bookings.CreateBookingDesktop.selectResource'))}
                    </div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showResourcePicker && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => setShowResourcePicker(false)} />
                    <div className={styles.listDropdown}>
                      {resourcesLoading && <div className={styles.listEmpty}>{t('desktop.pages.bookings.CreateBookingDesktop.loadingResources')}</div>}
                      {!resourcesLoading && Object.keys(groupedResources).length === 0 && (
                        <div className={styles.listEmpty}>{t('desktop.pages.bookings.CreateBookingDesktop.noResourcesAvailable')}</div>
                      )}
                      {Object.entries(groupedResources).map(([category, categoryResources]) => {
                        const filtered = allowedResourceTypeIds.length > 0
                          ? categoryResources.filter(r => r.type && allowedResourceTypeIds.includes(r.type))
                          : categoryResources;
                        if (filtered.length === 0) return null;
                        return (
                          <React.Fragment key={category}>
                            <div className={styles.listGroupHeader}>{translateDynamic(category)}</div>
                            {filtered.map(resource => (
                              <button
                                key={resource._id}
                                className={`${styles.listOption} ${resourceOption === (resource.name || resource._id) ? styles.listOptionSelected : ''}`}
                                onClick={() => { setResourceOption(resource.name || resource._id); setShowResourcePicker(false); }}
                              >
                                {resource.name || resource._id}
                                {resourceOption === (resource.name || resource._id) && <IonIcon icon={checkmark} className={styles.optionCheck} />}
                              </button>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Primary staff floating picker */}
              <div className={styles.pickerWrap}>
                <button
                  className={styles.fieldRow}
                  onClick={() => { setStaffPickerSlot(0); setStaffSearch(''); setShowStaffPicker(p => staffPickerSlot === 0 ? !p : true); setShowResourcePicker(false); }}
                >
                  <IonIcon icon={personOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.staffMember')}</div>
                    <div className={`${styles.fieldValue} ${!staffMembers[0] ? styles.fieldPlaceholder : ''}`}>
                      {staffMembers[0] || t('desktop.pages.bookings.CreateBookingDesktop.selectStaffMember')}
                    </div>
                  </div>
                  <IonIcon icon={chevronForwardOutline} className={styles.chevron} />
                </button>
                {showStaffPicker && staffPickerSlot === 0 && (
                  <>
                    <div className={styles.pickerBackdrop} onClick={() => { setShowStaffPicker(false); setStaffSearch(''); }} />
                    <div className={styles.staffDropdown}>
                      <input
                        className={styles.staffSearch}
                        placeholder={t('desktop.pages.bookings.CreateBookingDesktop.searchStaff')}
                        value={staffSearch}
                        onChange={e => setStaffSearch(e.target.value)}
                        autoFocus
                      />
                      {staffFetchLoading
                        ? <div className={styles.listEmpty}>{t('desktop.pages.bookings.CreateBookingDesktop.loading')}</div>
                        : allStaffMembers
                            .filter(s => s.name.toLowerCase().includes(staffSearch.toLowerCase()))
                            .map(s => (
                              <button
                                key={s._id}
                                className={`${styles.staffItem} ${staffMembers[0] === s.name ? styles.staffItemSelected : ''}`}
                                onClick={() => handleStaffMemberSelect(s.name)}
                              >
                                <img
                                  className={styles.staffAvatar}
                                  src={proxyImage(staffAvatarMap.get(s._id) ?? '') || getDefaultAvatar(s.name)}
                                  alt={s.name}
                                  onError={e => { (e.target as HTMLImageElement).src = getDefaultAvatar(s.name); }}
                                />
                                <span>{s.name}</span>
                                {staffMembers[0] === s.name && <IonIcon icon={checkmark} className={styles.optionCheck} />}
                              </button>
                            ))
                      }
                    </div>
                  </>
                )}
              </div>

              {/* Additional staff rows */}
              {staffMembers.slice(1).map((staff, index) => (
                <div key={index + 1} className={`${styles.fieldRow} ${styles.fieldRowStatic}`}>
                  <IonIcon icon={personOutline} className={styles.fieldIcon} />
                  <div className={styles.fieldContent}>
                    <div className={styles.fieldLabel}>{t('desktop.pages.bookings.CreateBookingDesktop.additionalStaff')}</div>
                    <div className={styles.fieldValue}>{staff}</div>
                  </div>
                  <button className={styles.removeBtn} onClick={() => handleRemoveStaffMember(index + 1)}>
                    <IonIcon icon={closeOutline} />
                  </button>
                </div>
              ))}

              {/* Add more staff — picker anchored below this button */}
              {staffMembers.length > 0 && (
                <div className={styles.pickerWrap}>
                  <button className={styles.addLineBtn} onClick={() => { handleAddStaffMember(); setShowResourcePicker(false); }}>
                    <IonIcon icon={addOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.addMoreStaff')}
                  </button>
                  {showStaffPicker && staffPickerSlot === -1 && (
                    <>
                      <div className={styles.pickerBackdrop} onClick={() => { setShowStaffPicker(false); setStaffSearch(''); }} />
                      <div className={styles.staffDropdown}>
                        <input
                          className={styles.staffSearch}
                          placeholder={t('desktop.pages.bookings.CreateBookingDesktop.searchStaff')}
                          value={staffSearch}
                          onChange={e => setStaffSearch(e.target.value)}
                          autoFocus
                        />
                        {staffFetchLoading
                          ? <div className={styles.listEmpty}>{t('desktop.pages.bookings.CreateBookingDesktop.loading')}</div>
                          : allStaffMembers
                              .filter(s => s.name.toLowerCase().includes(staffSearch.toLowerCase()))
                              .map(s => (
                                <button
                                  key={s._id}
                                  className={styles.staffItem}
                                  onClick={() => handleStaffMemberSelect(s.name)}
                                >
                                  <img
                                    className={styles.staffAvatar}
                                    src={proxyImage(staffAvatarMap.get(s._id) ?? '') || getDefaultAvatar(s.name)}
                                    alt={s.name}
                                    onError={e => { (e.target as HTMLImageElement).src = getDefaultAvatar(s.name); }}
                                  />
                                  <span>{s.name}</span>
                                </button>
                              ))
                        }
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Notes Card */}
            <div className={styles.card}>
              <div className={styles.cardTitle}>{t('desktop.pages.bookings.CreateBookingDesktop.notes')}</div>
              {!notesAdded ? (
                <button className={styles.addLineBtn} onClick={() => setNotesAdded(true)}>
                  <IonIcon icon={addOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.addBookingNotes')}
                </button>
              ) : (
                <>
                  <textarea
                    className={styles.textarea}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder={t('desktop.pages.bookings.CreateBookingDesktop.addAnySpecialInstructionsOrNotes')}
                    rows={4}
                  />
                  <button
                    className={styles.removeLinkBtn}
                    onClick={() => { setNotes(''); setNotesAdded(false); }}
                  >
                    <IonIcon icon={trashOutline} />{t('desktop.pages.bookings.CreateBookingDesktop.removeNotes')}
                  </button>
                </>
              )}
            </div>

          </div>
        </div>

        {/* ── Sub-modals (all logic unchanged) ──────────────────────────── */}

        <ServiceTypeModal
          isOpen={isServiceTypeModalOpen}
          onClose={() => setIsServiceTypeModalOpen(false)}
          onSelectServiceType={handleServiceTypeSelectWithIndex}
          selectedServiceType={serviceType}
          services={serviceTypeOptions}
          loading={serviceTypesLoading}
        />


      </IonContent>
    </IonPage>
  );
};

export default CreateBookingDesktop;
